import * as React from 'react'
import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import PictureAsPdfOutlinedIcon from '@material-ui/icons/PictureAsPdfOutlined'
import {
  Calculator,
  HeroSection,
  HomeSlider,
  IconList,
  LinkWithIcon,
  ProductInfoVariationForty,
  ProductInfoVariationHundred,
  ProductInfoVariationSeventy,
  ProductInfoVariationSixty,
  ProductInfoVariationThirty,
  ProductInfoVariationTwenty,
  Slash,
  XRayText,
} from '../../components'
import { contentfulArrayToObject, getDescription } from '../../utils/cms'
import { useStyles } from './leasingDetailsView.styles'
import { useHeroStyles } from '../home/Home.styles'
import { SLASH_HEIGHT } from '../../constants'
import {
  conditionIconListData,
  conditionIconListDataPersonalInstant,
  conditionIconListDataPersonalStandard,
  requiredIconListData,
  requiredIconListDataPersonalInstant,
  requiredIconListDataPersonalStandard,
  requiredIconListSelfEmployedData,
  selfEmployedConditionIconListData,
} from './calculatorIconList.data'

import { productFeaturesData } from './productFeaturesData'
import ProductInfoVariationFortyAfterSales from '../../components/ProductInfo/ProductInfoVariation40AfterSales'
import ProductInfoVariationSixtyAfterSales from '../../components/ProductInfo/ProductInfoVariation60AfterSales'
import SingleSlider from '../../components/SingleSlider/SingleSlider'

const LeasingDetailsPage = ({
  t,
  language,
  footerNode,
  data = {},
  ...props
}) => {
  const styles = useStyles()
  const theme = useTheme()

  const slashHeight = SLASH_HEIGHT

  const newHeight = useMediaQuery(theme.breakpoints.down('sm'))
    ? slashHeight * 0.5
    : slashHeight
  const slashMaxGrowth = footerNode?.clientHeight / newHeight

  const { hero, sections } = data
  const {
    ContentfulSliderBlock: sliderBlock,
    ContentfulFeatureSection: featureSection,
    ContentfulHomepageSlider: singleSliderSection,
    ContentfulSectionText: section,
  } = contentfulArrayToObject(sections)

  // feature section
  const { featureHeading, featureCta } = featureSection || {}

  let iconListContent = (
    <>
      <IconList t={t} data={conditionIconListData} />
      <IconList t={t} data={requiredIconListData} />
    </>
  )

  switch (props.calculatorType) {
    case 'personal-finance-standard':
      iconListContent = (
        <>
          <IconList t={t} data={conditionIconListDataPersonalStandard} />
          <IconList t={t} data={requiredIconListDataPersonalStandard} />
        </>
      )
      break
    case 'personal-finance-instant':
      iconListContent = (
        <>
          <IconList t={t} data={conditionIconListDataPersonalInstant} />
          <IconList t={t} data={requiredIconListDataPersonalInstant} />
        </>
      )
      break
    case 'self-employed':
      iconListContent = (
        <>
          <IconList t={t} data={selfEmployedConditionIconListData} />
          <IconList t={t} data={requiredIconListSelfEmployedData} />
        </>
      )
      break
    default:
      break
  }

  return (
    <>
      <main className={styles.root}>
        <Slash
          stopGrowingPositionYOffset={footerNode?.clientHeight / 3}
          startGrowingPositionYOffset={footerNode?.clientHeight * 2.5}
          height={newHeight}
          growthScale={{ initial: 1, target: slashMaxGrowth }}
        />
        {hero && (
          <HeroSection
            homepage
            t={t}
            {...hero}
            useCustomStyles={useHeroStyles}
          />
        )}
        {featureSection && (
          <Box>
            <Container maxWidth="md">
              <Grid container className={styles.featureheading}>
                <Grid item md={6} xs={12}>
                  <XRayText isXray variant="h3" content={featureHeading} />
                </Grid>
                <Grid item md={6} xs={12} className={styles.featureLink}>
                  {featureCta && (
                    <LinkWithIcon
                      text={featureCta.title}
                      to={featureCta.link}
                      isExternal={featureCta.isExternal}
                    />
                  )}
                </Grid>
              </Grid>
            </Container>
            {props.productFeatureType && (
              <Container maxWidth="md" className={styles.productInfoContainer}>
                {productFeaturesData[props.productFeatureType].map(
                  (feature) => {
                    switch (feature.type) {
                      case 'TWENTY':
                        return <ProductInfoVariationTwenty {...feature} t={t} />
                      case 'THIRTY':
                        return <ProductInfoVariationThirty {...feature} t={t} />
                      case 'FORTY':
                        return <ProductInfoVariationForty {...feature} t={t} />
                      case 'SIXTY':
                        return <ProductInfoVariationSixty {...feature} t={t} />
                      case 'FORTY-AFTER-SALES':
                        return (
                          <ProductInfoVariationFortyAfterSales
                            {...feature}
                            t={t}
                          />
                        )
                      case 'SIXTY-AFTER-SALES':
                        return (
                          <ProductInfoVariationSixtyAfterSales
                            {...feature}
                            t={t}
                          />
                        )
                      case 'SEVENTY':
                        return (
                          <ProductInfoVariationSeventy {...feature} t={t} />
                        )
                      case 'HUNDRED':
                        return (
                          <ProductInfoVariationHundred {...feature} t={t} />
                        )
                      default:
                        return null
                    }
                  },
                )}
              </Container>
            )}
          </Box>
        )}

        {section && (
          <Box>
            <Container maxWidth="md">
              <Box className={styles.section}>
                <XRayText
                  variant="h3"
                  content={section.title}
                  textClass={styles.sectionHeading}
                />
                <Typography
                  className={styles.sectionDescription}
                  variant="body1"
                >
                  {getDescription(section.sectionText.raw)}
                </Typography>
                {section.link && <LinkWithIcon {...section.link} />}
                {props.calculatorType && (
                  <Box
                    className={styles.calculatorSection}
                    style={{ marginBottom: '80px' }}
                  >
                    <Calculator
                      location={props.location}
                      type={props.calculatorType}
                      t={t}
                    />
                    <Box className={styles.iconListsContainer}>
                      {iconListContent}
                    </Box>
                  </Box>
                )}
              </Box>
            </Container>
          </Box>
        )}
        {singleSliderSection && (
          <SingleSlider
            sliderData={singleSliderSection}
            buttonIcon={<PictureAsPdfOutlinedIcon />}
          />
        )}
        {sliderBlock?.sliderHeading && (
          <Box>
            <XRayText
              variant="h1"
              content={sliderBlock.sliderHeading}
              textClass={styles.sectionHeading}
            />
            <HomeSlider data={sliderBlock} />
          </Box>
        )}
      </main>
    </>
  )
}

export default LeasingDetailsPage
